import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Copy, Plus, Sparkles } from 'lucide-react';
import type { Block } from '@blogforge/shared';
import { useEditorStore } from '../stores/editorStore.js';
import { useAiComposer } from '../stores/aiComposer.js';
import { BlockRenderer } from './BlockRenderer.js';
import type { DragPath } from '../types.js';
import { useDraggableBlock } from '../hooks/use-draggable-block.js';
import { cn } from '../helpers.js';

interface Props {
  block: Block;
  path: DragPath;
  rawVariableDefinitions: Record<string, unknown> | undefined;
  /** Workspace audience attribute keys, when the document may merge them. */
  audienceAttributeKeys?: readonly string[] | undefined;
  /**
   * True when the block is the only one its container can hold — a list's item
   * template. Duplicating it or adding a block below it has nowhere to put a
   * second block (see the store's mutateListAt), so neither control is offered;
   * a container the author nests inside the template offers both as usual.
   */
  soleBlock?: boolean | undefined;
}

export function BlockWrapper({
  block,
  path,
  rawVariableDefinitions,
  audienceAttributeKeys,
  soleBlock = false,
}: Props) {
  const {
    selectedBlockId,
    hoveredBlockId,
    selectBlock,
    removeBlock,
    duplicateBlock,
    addBlock,
    setHoveredBlockId,
  } = useEditorStore();
  const isSelected = selectedBlockId?.[0] === block.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useDraggableBlock(block.id, block.type, path);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isHovered = hoveredBlockId === block.id;
  const controlsVisible = isHovered || isSelected;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-block-id={block.id}
      className={`block-wrapper group relative ${isSelected ? 'block-selected' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        console.log('select block: ', block.id, path);
        selectBlock([block.id, path]);
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        setHoveredBlockId(block.id);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setHoveredBlockId(null);
      }}
    >
      {/* Drag handle */}
      <div
        className={cn('block-drag-handle', { active: controlsVisible })}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </div>

      {/* Block actions — appear on hover */}
      <div
        className={cn(
          'absolute top-0 -right-2 z-10 flex translate-x-full flex-col gap-1 transition-opacity',
          {
            'opacity-0': !controlsVisible,
            'opacity-100': controlsVisible,
          },
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            selectBlock([block.id, path]);
            useAiComposer.getState().openComposer({ blockId: block.id, path });
          }}
          className="border-line bg-card text-muted hover:border-accent-border hover:bg-accent/10 hover:text-accent flex h-6 w-6 items-center justify-center rounded border shadow-sm"
          title="Rewrite this block with AI"
        >
          <Sparkles size={11} />
        </button>
        {!soleBlock && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              duplicateBlock(block.id, path);
            }}
            className="border-line bg-card text-muted hover:bg-bg-soft hover:text-ink-soft flex h-6 w-6 items-center justify-center rounded border shadow-sm"
            title="Duplicate block"
          >
            <Copy size={11} />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            // eslint-disable-next-line no-restricted-globals
            if (confirm('Remove this block?')) removeBlock(block.id, path);
          }}
          className="border-line bg-card text-muted hover:bg-danger/8 hover:text-danger flex h-6 w-6 items-center justify-center rounded border shadow-sm"
          title="Remove block"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {/* The actual block content */}
      <BlockRenderer
        block={block}
        path={path}
        rawVariableDefinitions={rawVariableDefinitions}
        audienceAttributeKeys={audienceAttributeKeys}
      />

      {/* Add block below — appears on hover */}
      {!soleBlock && (
        <div
          className={cn('absolute -bottom-3 left-1/2 z-10 -translate-x-1/2 transition-opacity', {
            'opacity-0': !controlsVisible,
            'opacity-100': controlsVisible,
          })}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              addBlock({ type: 'paragraph', content: '', attrs: {} }, block.id, path);
            }}
            className="bg-accent text-accent-ink shadow-glow flex h-6 w-6 items-center justify-center rounded-full transition-transform hover:scale-110"
            title="Add block below"
          >
            <Plus size={11} strokeWidth={2.6} />
          </button>
        </div>
      )}
    </div>
  );
}
